import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { DefaultView } from "@redotech/effect-api-shared/models/support-ticket/default-view";
import {
  getSupportTicketLeafFilters,
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { SPAM_TAG } from "@redotech/redo-model/conversation-tags/conversation-tag-record";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import {
  type NumberFilterData,
  NumberFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/number-filter";
import type { FilterGroup } from "@redotech/redo-model/views/views";

export const DEFAULT_VIEW_DISPLAY_NAMES: Record<DefaultView, string> = {
  [DefaultView.OPEN]: "Open",
  [DefaultView.ASSIGNED]: "Assigned to me",
  [DefaultView.MENTIONS]: "Mentions",
  [DefaultView.SENT]: "Sent",
  [DefaultView.DRAFTS]: "Drafts",
  [DefaultView.AI_CONVERSATIONS]: "AI conversations",
  [DefaultView.SPAM]: "Spam",
  [DefaultView.ARCHIVED]: "Archived",
};

/**
 * NONE_OF clause that hides spam-tagged tickets. Applied to every view except
 * the dedicated Spam view so merchants don't see spam in Open/Drafts/etc.
 */
export const SPAM_TAG_EXCLUSION_FILTER: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.SUPPORT_TICKET_TAGS
  ] as ArrayStringFilterData),
  value: [SPAM_TAG],
  operator: ArrayStringFilterOperator.NONE_OF,
};

/**
 * Returns true if the filter group already references the spam tag (in either
 * direction). Callers use this to skip layering in the exclusion on views that
 * are intentionally filtering on spam.
 */
export function filterGroupReferencesSpamTag(group: FilterGroup): boolean {
  return getSupportTicketLeafFilters(group).some((filter) => {
    if (filter.name !== SupportTicketFilterNames.SUPPORT_TICKET_TAGS) {
      return false;
    }
    const value = (filter as ArrayStringFilterData).value ?? [];
    return value.includes(SPAM_TAG);
  });
}

/**
 * ANDs the spam-tag exclusion into the filter group, unless the group already
 * references the spam tag (e.g. the Spam view). Passing `null` returns a group
 * containing only the exclusion clause.
 */
export function withSpamTagExclusion(group: FilterGroup | null): FilterGroup {
  if (group && filterGroupReferencesSpamTag(group)) {
    return group;
  }
  if (!group) {
    return {
      operator: FilterOperator.AND,
      clauses: [SPAM_TAG_EXCLUSION_FILTER],
    };
  }
  if (group.operator === FilterOperator.AND) {
    return { ...group, clauses: [...group.clauses, SPAM_TAG_EXCLUSION_FILTER] };
  }
  return {
    operator: FilterOperator.AND,
    clauses: [group, SPAM_TAG_EXCLUSION_FILTER],
  };
}

/**
 * Returns true if the filter is the implicit spam-tag exclusion clause.
 * Used to hide the clause from active-filter pill UI since it is applied
 * automatically, not chosen by the user.
 */
export function isSpamTagExclusionFilter(filter: {
  name: string;
  value?: unknown;
  operator?: unknown;
}): boolean {
  if (filter.name !== SupportTicketFilterNames.SUPPORT_TICKET_TAGS) {
    return false;
  }
  if (filter.operator !== ArrayStringFilterOperator.NONE_OF) {
    return false;
  }
  const value = (filter as ArrayStringFilterData).value ?? [];
  return value.length === 1 && value[0] === SPAM_TAG;
}

const openStatusFilter: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

export const OPEN_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [openStatusFilter, SPAM_TAG_EXCLUSION_FILTER],
};

const aiAssigneeAnyOfFilter: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.ASSIGNEE_TYPE
  ] as ArrayStringFilterData),
  value: ["AI"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

export const AI_CONVERSATIONS_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [aiAssigneeAnyOfFilter, SPAM_TAG_EXCLUSION_FILTER],
};

export function buildAssignedViewFilterGroup(userId: string): FilterGroup {
  const assigneesFilter: ArrayStringFilterData = {
    ...(supportTicketFilterData[
      SupportTicketFilterNames.ASSIGNEES
    ] as ArrayStringFilterData),
    value: [MongoUserId.fromString(userId)],
    operator: ArrayStringFilterOperator.ANY_OF,
  };
  return {
    operator: FilterOperator.AND,
    clauses: [openStatusFilter, assigneesFilter, SPAM_TAG_EXCLUSION_FILTER],
  };
}

export function buildMentionsViewFilterGroup(userId: string): FilterGroup {
  const mentionsFilter: ArrayStringFilterData = {
    ...(supportTicketFilterData[
      SupportTicketFilterNames.MENTIONS
    ] as ArrayStringFilterData),
    value: [MongoUserId.fromString(userId)],
    operator: ArrayStringFilterOperator.ANY_OF,
  };
  return {
    operator: FilterOperator.AND,
    clauses: [openStatusFilter, mentionsFilter],
  };
}

const numSentMessagesFilter: NumberFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.NUM_SENT_MESSAGES
  ] as NumberFilterData),
  value: [0],
  operator: NumberFilterOperator.GREATER_THAN,
};

export const SENT_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [numSentMessagesFilter, SPAM_TAG_EXCLUSION_FILTER],
};

const numDraftsFilter: NumberFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.NUM_DRAFTS
  ] as NumberFilterData),
  value: [0],
  operator: NumberFilterOperator.GREATER_THAN,
};

export const DRAFTS_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [openStatusFilter, numDraftsFilter, SPAM_TAG_EXCLUSION_FILTER],
};

export const SPAM_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [
    {
      ...(supportTicketFilterData[
        SupportTicketFilterNames.SUPPORT_TICKET_TAGS
      ] as ArrayStringFilterData),
      value: [SPAM_TAG],
      operator: ArrayStringFilterOperator.ANY_OF,
    },
  ],
};

export const ARCHIVED_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [
    {
      ...(supportTicketFilterData[
        SupportTicketFilterNames.STATUS
      ] as ArrayStringFilterData),
      value: ["ARCHIVED"],
      operator: ArrayStringFilterOperator.ANY_OF,
    },
    SPAM_TAG_EXCLUSION_FILTER,
  ],
};

export {
  DefaultView,
  isDefaultView,
} from "@redotech/effect-api-shared/models/support-ticket/default-view";
